import React, { useEffect, useRef, useCallback, useState } from 'react';
import './FocusManager.css';

/**
 * FocusManager component for advanced focus management
 * Based on ArbiWin design system and WCAG guidelines
 *
 * @param {Object} props - FocusManager properties
 * @param {React.ReactNode} props.children - Child components
 * @param {boolean} props.trapFocus - Whether to trap focus within the container
 * @param {boolean} props.restoreFocus - Whether to restore focus when unmounting
 * @param {boolean} props.autoFocus - Whether to auto-focus first focusable element
 * @param {string} props.initialFocus - Selector for initial focus element
 * @param {Function} props.onFocusEnter - Callback when focus enters container
 * @param {Function} props.onFocusLeave - Callback when focus leaves container
 * @param {boolean} props.disabled - Whether focus management is disabled
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} FocusManager component
 */
const FocusManager = function FocusManager({
  children,
  trapFocus = false,
  restoreFocus = true,
  autoFocus = false,
  initialFocus,
  onFocusEnter,
  onFocusLeave,
  disabled = false,
  className = '',
  testId
}) {

  const containerRef = useRef(null);
  const previousFocusRef = useRef(null);
  const [focusableElements, setFocusableElements] = useState([]);
  const [currentFocusIndex, setCurrentFocusIndex] = useState(-1);

  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
    'audio[controls]',
    'video[controls]',
    'details summary'
  ].join(', ');

  // Get all focusable elements within the container
  const getFocusableElements = useCallback(function() {
    if (!containerRef.current) return [];

    const elements = Array.from(
      containerRef.current.querySelectorAll(focusableSelectors)
    ).filter(function(element) {
      return (
        element.offsetWidth > 0 &&
        element.offsetHeight > 0 &&
        !element.disabled &&
        element.getAttribute('tabindex') !== '-1' &&
        window.getComputedStyle(element).visibility !== 'hidden' &&
        window.getComputedStyle(element).display !== 'none'
      );
    });

    return elements;
  }, [focusableSelectors]);

  // Update focusable elements when children change
  useEffect(function() {
    if (disabled) return;

    const updateFocusableElements = function() {
      const elements = getFocusableElements();
      setFocusableElements(elements);
    };

    updateFocusableElements();

    // Use MutationObserver to detect DOM changes
    const observer = new MutationObserver(updateFocusableElements);
    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'tabindex', 'hidden']
      });
    }

    return function() {
      observer.disconnect();
    };
  }, [children, disabled, getFocusableElements]);

  // Handle initial focus
  useEffect(function() {
    if (disabled || !autoFocus) return;

    const focusInitialElement = function() {
      let elementToFocus = null;

      if (initialFocus) {
        elementToFocus = containerRef.current?.querySelector(initialFocus);
      }

      if (!elementToFocus && focusableElements.length > 0) {
        elementToFocus = focusableElements[0];
      }

      if (elementToFocus) {
        elementToFocus.focus();
        setCurrentFocusIndex(0);
      }
    };

    // Small delay to ensure DOM is ready
    setTimeout(focusInitialElement, 50);
  }, [autoFocus, initialFocus, focusableElements, disabled]);

  // Store previous focus for restoration
  useEffect(function() {
    if (disabled || !restoreFocus) return;

    previousFocusRef.current = document.activeElement;

    return function() {
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus();
      }
    };
  }, [restoreFocus, disabled]);

  // Handle focus trapping
  useEffect(function() {
    if (disabled || !trapFocus || focusableElements.length === 0) return;

    const handleKeyDown = function(event) {
      if (event.key !== 'Tab') return;

      const currentIndex = focusableElements.indexOf(document.activeElement);
      
      if (event.shiftKey) {
        // Shift + Tab (backward)
        if (currentIndex <= 0) {
          event.preventDefault();
          focusableElements[focusableElements.length - 1].focus();
          setCurrentFocusIndex(focusableElements.length - 1);
        } else {
          setCurrentFocusIndex(currentIndex - 1);
        }
      } else {
        // Tab (forward)
        if (currentIndex >= focusableElements.length - 1) {
          event.preventDefault();
          focusableElements[0].focus();
          setCurrentFocusIndex(0);
        } else {
          setCurrentFocusIndex(currentIndex + 1);
        }
      }
    };

    const handleFocusIn = function(event) {
      if (!containerRef.current?.contains(event.target)) {
        // Focus moved outside container, bring it back
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
          setCurrentFocusIndex(0);
        }
      } else {
        const index = focusableElements.indexOf(event.target);
        setCurrentFocusIndex(index);
        
        if (onFocusEnter && index === 0) {
          onFocusEnter(event);
        }
      }
    };

    const handleFocusOut = function(event) {
      // Check if focus is moving outside the container
      if (!containerRef.current?.contains(event.relatedTarget)) {
        if (onFocusLeave) {
          onFocusLeave(event);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('focusin', handleFocusIn);
    containerRef.current?.addEventListener('focusout', handleFocusOut);

    return function() {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', handleFocusIn);
      containerRef.current?.removeEventListener('focusout', handleFocusOut);
    };
  }, [trapFocus, focusableElements, onFocusEnter, onFocusLeave, disabled]);

  const focusManagerClasses = [
    'arbiwin-focus-manager',
    trapFocus ? 'arbiwin-focus-manager--trap' : '',
    disabled ? 'arbiwin-focus-manager--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  // Public API for programmatic focus management
  const focusFirst = useCallback(function() {
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
      setCurrentFocusIndex(0);
    }
  }, [focusableElements]);

  const focusLast = useCallback(function() {
    if (focusableElements.length > 0) {
      const lastIndex = focusableElements.length - 1;
      focusableElements[lastIndex].focus();
      setCurrentFocusIndex(lastIndex);
    }
  }, [focusableElements]);

  const focusNext = useCallback(function() {
    if (focusableElements.length === 0) return;
    
    const nextIndex = currentFocusIndex < focusableElements.length - 1 
      ? currentFocusIndex + 1 
      : 0;
    
    focusableElements[nextIndex].focus();
    setCurrentFocusIndex(nextIndex);
  }, [focusableElements, currentFocusIndex]);

  const focusPrevious = useCallback(function() {
    if (focusableElements.length === 0) return;
    
    const prevIndex = currentFocusIndex > 0 
      ? currentFocusIndex - 1 
      : focusableElements.length - 1;
    
    focusableElements[prevIndex].focus();
    setCurrentFocusIndex(prevIndex);
  }, [focusableElements, currentFocusIndex]);

  const focusElement = useCallback(function(selector) {
    const element = containerRef.current?.querySelector(selector);
    if (element) {
      element.focus();
      const index = focusableElements.indexOf(element);
      setCurrentFocusIndex(index);
    }
  }, [focusableElements]);

  // Expose API through ref
  useEffect(function() {
    if (containerRef.current) {
      containerRef.current.focusManager = {
        focusFirst,
        focusLast,
        focusNext,
        focusPrevious,
        focusElement,
        getFocusableElements,
        currentFocusIndex,
        focusableCount: focusableElements.length
      };
    }
  }, [
    focusFirst,
    focusLast,
    focusNext,
    focusPrevious,
    focusElement,
    getFocusableElements,
    currentFocusIndex,
    focusableElements.length
  ]);

  return (
    <div
      ref={containerRef}
      className={focusManagerClasses}
      data-testid={testId || 'arbiwin-focus-manager'}
      data-focus-trap={trapFocus}
      data-focusable-count={focusableElements.length}
    >
      {children}
    </div>
  );
};

/**
 * FocusTrap component - Simple focus trap wrapper
 *
 * @param {Object} props - FocusTrap properties
 * @param {React.ReactNode} props.children - Child components
 * @param {boolean} props.active - Whether trap is active
 * @param {Function} props.onEscape - Escape key handler
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} FocusTrap component
 */
const FocusTrap = function FocusTrap({
  children,
  active = true,
  onEscape,
  className = ''
}) {

  const handleKeyDown = useCallback(function(event) {
    if (event.key === 'Escape' && onEscape) {
      onEscape(event);
    }
  }, [onEscape]);

  useEffect(function() {
    if (active && onEscape) {
      document.addEventListener('keydown', handleKeyDown);
      return function() {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [active, handleKeyDown, onEscape]);

  return (
    <FocusManager
      trapFocus={active}
      restoreFocus={active}
      autoFocus={active}
      className={'arbiwin-focus-trap ' + className}
    >
      {children}
    </FocusManager>
  );
};

/**
 * useFocusManagement hook for focus management utilities
 *
 * @param {Object} options - Hook options
 * @param {boolean} options.trapFocus - Whether to trap focus
 * @param {boolean} options.restoreFocus - Whether to restore focus
 * @returns {Object} Focus management utilities
 */
const useFocusManagement = function(options = {}) {
  const {
    trapFocus = false,
    restoreFocus = true
  } = options;

  const containerRef = useRef(null);
  const previousFocusRef = useRef(null);

  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]'
  ].join(', ');

  const getFocusableElements = useCallback(function() {
    if (!containerRef.current) return [];

    return Array.from(
      containerRef.current.querySelectorAll(focusableSelectors)
    ).filter(function(element) {
      return (
        element.offsetWidth > 0 &&
        element.offsetHeight > 0 &&
        !element.disabled
      );
    });
  }, [focusableSelectors]);

  const focusFirst = useCallback(function() {
    const elements = getFocusableElements();
    if (elements.length > 0) {
      elements[0].focus();
    }
  }, [getFocusableElements]);

  const focusLast = useCallback(function() {
    const elements = getFocusableElements();
    if (elements.length > 0) {
      elements[elements.length - 1].focus();
    }
  }, [getFocusableElements]);

  const storeFocus = useCallback(function() {
    previousFocusRef.current = document.activeElement;
  }, []);

  const restoreFocusRef = useCallback(function() {
    if (restoreFocus && previousFocusRef.current) {
      previousFocusRef.current.focus();
    }
  }, [restoreFocus]);

  return {
    containerRef,
    focusFirst,
    focusLast,
    getFocusableElements,
    storeFocus,
    restoreFocus: restoreFocusRef
  };
};

FocusManager.displayName = 'FocusManager';
FocusTrap.displayName = 'FocusTrap';

export default FocusManager;
export { FocusTrap, useFocusManagement };
